import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import StatsCard from '../admin-dashboard/components/StatsCard';
import SalesChart from './components/SalesChart';
import TopProductsTable from './components/TopProductsTable';
import PaymentMethodBreakdown from './components/PaymentMethodBreakdown';
import InventoryAlerts from './components/InventoryAlerts';
import StaffPerformance from './components/StaffPerformance';

const SalesAnalyticsReporting = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalTransactions: 0,
    averageTransaction: 0,
    totalItemsSold: 0,
    revenueGrowth: 0,
    transactionGrowth: 0
  });
  const [salesData, setSalesData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [paymentBreakdown, setPaymentBreakdown] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [staffPerformance, setStaffPerformance] = useState([]);

  const dateRangeOptions = [
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 90 Days' },
    { value: '365', label: 'Last Year' }
  ];

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isStaff))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isStaff, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isStaff)) {
      loadAnalyticsData();
    }
  }, [currentTenant, isGymOwner, isStaff, dateRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSalesStats(),
        loadSalesChart(),
        loadTopProducts(),
        loadPaymentBreakdown(),
        loadLowStockAlerts(),
        loadStaffPerformance()
      ]);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSalesStats = async () => {
    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      const { data: transactions, error } = await supabase?.from('pos_transactions')?.select('total_amount, transaction_date, payment_status')?.eq('tenant_id', currentTenant?.id)?.eq('payment_status', 'completed')?.gte('transaction_date', startDate?.toISOString());

      if (error) throw error;

      const totalRevenue = transactions?.reduce((sum, t) => sum + parseFloat(t?.total_amount || 0), 0) || 0;
      const totalTransactions = transactions?.length || 0;
      const averageTransaction = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

      // Get items sold count
      const { data: items, error: itemsError } = await supabase?.from('pos_transaction_items')?.select('quantity, transaction_id')?.in('transaction_id', transactions?.map(t => t?.id) || []);

      if (!itemsError) {
        const totalItemsSold = items?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0;
        
        // Calculate growth (compare to previous period)
        const previousStartDate = new Date(startDate);
        previousStartDate?.setDate(previousStartDate?.getDate() - daysAgo);
        
        const { data: previousTransactions } = await supabase?.from('pos_transactions')?.select('total_amount')?.eq('tenant_id', currentTenant?.id)?.eq('payment_status', 'completed')?.gte('transaction_date', previousStartDate?.toISOString())?.lt('transaction_date', startDate?.toISOString());

        const previousRevenue = previousTransactions?.reduce((sum, t) => sum + parseFloat(t?.total_amount || 0), 0) || 0;
        const revenueGrowth = previousRevenue > 0 ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 : 0;
        const transactionGrowth = previousTransactions?.length > 0 ? ((totalTransactions - previousTransactions?.length) / previousTransactions?.length) * 100 : 0;

        setStats({
          totalRevenue,
          totalTransactions,
          averageTransaction,
          totalItemsSold,
          revenueGrowth,
          transactionGrowth
        });
      }
    } catch (error) {
      console.error('Error loading sales stats:', error);
    }
  };

  const loadSalesChart = async () => {
    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      const { data, error } = await supabase?.from('pos_transactions')?.select('total_amount, transaction_date')?.eq('tenant_id', currentTenant?.id)?.eq('payment_status', 'completed')?.gte('transaction_date', startDate?.toISOString())?.order('transaction_date', { ascending: true });

      if (error) throw error;

      // Group by date
      const chartMap = {};
      data?.forEach(t => {
        const date = t?.transaction_date?.split('T')?.[0];
        if (!chartMap?.[date]) {
          chartMap[date] = { date, revenue: 0, transactions: 0 };
        }
        chartMap[date].revenue += parseFloat(t?.total_amount || 0);
        chartMap[date].transactions += 1;
      });

      setSalesData(Object.values(chartMap));
    } catch (error) {
      console.error('Error loading sales chart:', error);
    }
  };

  const loadTopProducts = async () => {
    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      const { data: transactions } = await supabase?.from('pos_transactions')?.select('id')?.eq('tenant_id', currentTenant?.id)?.eq('payment_status', 'completed')?.gte('transaction_date', startDate?.toISOString());

      if (transactions?.length > 0) {
        const { data: items, error } = await supabase?.from('pos_transaction_items')?.select('product_id, product_name, quantity, subtotal')?.in('transaction_id', transactions?.map(t => t?.id));

        if (error) throw error;

        // Aggregate by product
        const productMap = {};
        items?.forEach(item => {
          if (!productMap?.[item?.product_id]) {
            productMap[item?.product_id] = {
              product_id: item?.product_id,
              product_name: item?.product_name,
              quantity_sold: 0,
              revenue: 0
            };
          }
          productMap[item?.product_id].quantity_sold += item?.quantity || 0;
          productMap[item?.product_id].revenue += parseFloat(item?.subtotal || 0);
        });

        const sortedProducts = Object.values(productMap)?.sort((a, b) => b?.revenue - a?.revenue)?.slice(0, 10);

        setTopProducts(sortedProducts);
      }
    } catch (error) {
      console.error('Error loading top products:', error);
    }
  };

  const loadPaymentBreakdown = async () => {
    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      const { data, error } = await supabase?.from('pos_transactions')?.select('payment_method, total_amount')?.eq('tenant_id', currentTenant?.id)?.eq('payment_status', 'completed')?.gte('transaction_date', startDate?.toISOString());

      if (error) throw error;

      const breakdown = {};
      data?.forEach(t => {
        const method = t?.payment_method || 'unknown';
        if (!breakdown?.[method]) {
          breakdown[method] = { method, amount: 0, count: 0 };
        }
        breakdown[method].amount += parseFloat(t?.total_amount || 0);
        breakdown[method].count += 1;
      });

      setPaymentBreakdown(Object.values(breakdown));
    } catch (error) {
      console.error('Error loading payment breakdown:', error);
    }
  };

  const loadLowStockAlerts = async () => {
    try {
      const { data, error } = await supabase?.from('pos_products')?.select('id, name, sku, stock_quantity, low_stock_threshold')?.eq('tenant_id', currentTenant?.id)?.eq('is_active', true)?.eq('track_inventory', true)?.order('stock_quantity', { ascending: true })?.limit(10);

      if (error) throw error;

      const lowStock = data?.filter(p => p?.stock_quantity <= p?.low_stock_threshold) || [];
      setLowStockProducts(lowStock);
    } catch (error) {
      console.error('Error loading low stock alerts:', error);
    }
  };

  const loadStaffPerformance = async () => {
    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      const { data: transactions, error } = await supabase?.from('pos_transactions')?.select('cashier_id, total_amount, user_profiles:cashier_id(full_name)')?.eq('tenant_id', currentTenant?.id)?.eq('payment_status', 'completed')?.gte('transaction_date', startDate?.toISOString());

      if (error) throw error;

      const staffMap = {};
      transactions?.forEach(t => {
        const cashierId = t?.cashier_id;
        if (!staffMap?.[cashierId]) {
          staffMap[cashierId] = {
            cashier_id: cashierId,
            name: t?.user_profiles?.full_name || 'Unknown',
            sales: 0,
            transactions: 0
          };
        }
        staffMap[cashierId].sales += parseFloat(t?.total_amount || 0);
        staffMap[cashierId].transactions += 1;
      });

      const sortedStaff = Object.values(staffMap)?.sort((a, b) => b?.sales - a?.sales);

      setStaffPerformance(sortedStaff);
    } catch (error) {
      console.error('Error loading staff performance:', error);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Sales Analytics & Reporting</h1>
            <p className="text-muted-foreground">Comprehensive sales insights and performance metrics</p>
          </div>
          <div className="flex items-center gap-3">
            <Select
              value={dateRange}
              onChange={setDateRange}
              options={dateRangeOptions}
              className="w-48"
            />
            <Button
              iconName="Download"
              variant="outline"
              onClick={() => alert('Export functionality coming soon')}
            >
              Export Report
            </Button>
          </div>
        </div>

        {/* KPI Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <StatsCard
            icon="DollarSign"
            iconColor="#10b981"
            title="Total Revenue"
            value={`$${stats?.totalRevenue?.toFixed(2)}`}
            subtitle={`${dateRange} days period`}
            trend={stats?.revenueGrowth > 0 ? 'up' : stats?.revenueGrowth < 0 ? 'down' : 'neutral'}
            trendValue={`${Math.abs(stats?.revenueGrowth)?.toFixed(1)}%`}
          />
          <StatsCard
            icon="ShoppingCart"
            iconColor="#3b82f6"
            title="Total Transactions"
            value={stats?.totalTransactions?.toString()}
            subtitle="Completed sales"
            trend={stats?.transactionGrowth > 0 ? 'up' : stats?.transactionGrowth < 0 ? 'down' : 'neutral'}
            trendValue={`${Math.abs(stats?.transactionGrowth)?.toFixed(1)}%`}
          />
          <StatsCard
            icon="TrendingUp"
            iconColor="#8b5cf6"
            title="Average Transaction"
            value={`$${stats?.averageTransaction?.toFixed(2)}`}
            subtitle="Per sale"
            trend="neutral"
            trendValue="0%"
          />
          <StatsCard
            icon="Package"
            iconColor="#f59e0b"
            title="Items Sold"
            value={stats?.totalItemsSold?.toString()}
            subtitle="Total units"
            trend="neutral"
            trendValue="0%"
          />
        </div>

        {/* Sales Chart */}
        <div className="mb-6">
          <SalesChart data={salesData} />
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <TopProductsTable products={topProducts} />
          <PaymentMethodBreakdown data={paymentBreakdown} />
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <InventoryAlerts products={lowStockProducts} />
          <StaffPerformance staff={staffPerformance} />
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default SalesAnalyticsReporting;